import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { map, merge } from 'lodash';

import { normalizeChangesWithNestedObjects } from '@/utils/utils';
import { IWorkItem } from '@/apps/dispatcher/types/IWorkItem';

export interface QueuedHistoryDateRange {
  from: string;
  to: string;
}

interface QueuedHistoryState {
  dateRange: QueuedHistoryDateRange | null;
  results: IWorkItem[];
  loading: boolean;
  // "The fetch failed" vs "the range holds nothing": both leave `results`
  // empty, and the grid says something different about each.
  failed: boolean;
  // Set once the slice holds an answer for the current range. Until then an
  // empty `results` means the fetch has not run - it is dispatched from an
  // effect, a frame after the range lands here.
  settled: boolean;
}

export const initialQueuedHistoryState: QueuedHistoryState = {
  dateRange: null,
  results: [],
  loading: false,
  failed: false,
  settled: false,
};

const queuedHistorySlice = createSlice({
  name: 'queuedHistory',
  initialState: initialQueuedHistoryState,
  reducers: {
    setQueuedHistoryDateRange(
      state,
      action: PayloadAction<QueuedHistoryDateRange>
    ) {
      // Re-picking the identical range keeps its answer: the fetch dedups on
      // identical params, so nothing would settle it a second time.
      const isNewRange =
        state.dateRange?.from !== action.payload.from ||
        state.dateRange?.to !== action.payload.to;

      state.dateRange = action.payload;

      if (isNewRange) {
        state.settled = false;
      }
    },
    setQueuedHistoryLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
      if (action.payload) {
        // A new attempt clears the previous failure, so no spinner sits on top
        // of a stale error state.
        state.failed = false;
      }
    },
    setQueuedHistoryResults(state, action: PayloadAction<IWorkItem[]>) {
      state.results = action.payload;
      state.loading = false;
      state.failed = false;
      state.settled = true;
    },
    // Drops the previous range's results, which must not linger under the new
    // range's label.
    setQueuedHistoryFailed(state) {
      state.results = initialQueuedHistoryState.results;
      state.loading = false;
      state.failed = true;
      state.settled = true;
    },
    // Keeps a result in sync after the user's own edit (e.g. resolved or
    // sent to PTA from within the history search) - a no-op if the item
    // isn't among the current results. Rendering then filters by status,
    // so a resolved item disappears the same way it would from the live
    // queue.
    updateQueuedHistoryResult(state, action: PayloadAction<IWorkItem>) {
      state.results = map(state.results, (item) =>
        item.id === action.payload.id ? action.payload : item
      );
    },
    // Merge-style sync driven by the SignalR API.Update/API.Batch
    // broadcasts, mirroring `signalWorkItemUpdate` in workItemSlice -
    // silently a no-op when the item isn't among the current results
    // (unlike the live queue, absence is expected here: the search only
    // holds items for the requested date range).
    signalQueuedHistoryUpdate(
      state,
      action: PayloadAction<{
        id: number | string;
        changes: Partial<IWorkItem>;
      }>
    ) {
      const { id, changes } = action.payload;
      const index = state.results.findIndex((item) => item.id === id);

      if (index !== -1) {
        const normalizedChanges = normalizeChangesWithNestedObjects(changes);
        state.results[index] = merge(
          {},
          state.results[index],
          normalizedChanges
        ) as IWorkItem;
      }
    },
    // Driven by the SignalR API.Delete broadcast so deleted items don't
    // stay visible and actionable in the history grid - a no-op when the
    // item isn't among the current results.
    removeQueuedHistoryResult(state, action: PayloadAction<number | string>) {
      state.results = state.results.filter(
        (item) => item.id !== action.payload
      );
    },
    resetQueuedHistorySearch(state) {
      state.dateRange = initialQueuedHistoryState.dateRange;
      state.results = initialQueuedHistoryState.results;
      state.loading = initialQueuedHistoryState.loading;
      state.failed = initialQueuedHistoryState.failed;
      state.settled = initialQueuedHistoryState.settled;
    },
  },
});

export const {
  setQueuedHistoryDateRange,
  setQueuedHistoryLoading,
  setQueuedHistoryResults,
  setQueuedHistoryFailed,
  updateQueuedHistoryResult,
  signalQueuedHistoryUpdate,
  removeQueuedHistoryResult,
  resetQueuedHistorySearch,
} = queuedHistorySlice.actions;

export default queuedHistorySlice.reducer;
